/**
 * Modal component selector for the Hybrid System Integration Tool.
 * @module HybridSystemIntegration/components/ComponentPalette
 */

import React from 'react';
import { Button, Modal, Tag, Typography } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { COMPONENT_LIBRARY } from '../constants';
import styles from '../HybridSystemIntegration.module.css';

const { Text, Title } = Typography;

function PaletteItem({ component, onAdd }) {
  const { t } = useTranslation();

  return (
    <button
      type="button"
      className={styles.paletteModalItem}
      onClick={() => onAdd(component.type)}
    >
      <span className={styles.paletteIcon} style={{ backgroundColor: component.color }}>
        {component.icon}
      </span>
      <span className={styles.paletteText}>
        <Text strong>{t(component.translationKey)}</Text>
        <Tag className={styles.categoryTag}>{t(component.categoryTranslationKey)}</Tag>
      </span>
      <span className={styles.paletteAction}>
        <PlusOutlined />
      </span>
    </button>
  );
}

/**
 * Component chooser modal.
 * @param {Object} props - Component props.
 * @returns {JSX.Element} Modal palette.
 */
export function ComponentPalette({ open, onClose, onAddComponent }) {
  const { t } = useTranslation();
  const components = Object.values(COMPONENT_LIBRARY);

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      width={720}
      destroyOnClose
      title={t('hybridSystemTool.palette.title')}
    >
      <div className={styles.paletteModalHeader}>
        <Title level={5}>{t('hybridSystemTool.actions.addComponent')}</Title>
        <Text type="secondary">Choose a component to place on the canvas.</Text>
      </div>
      <div className={styles.paletteModalGrid}>
        {components.map((component) => (
          <PaletteItem
            key={component.type}
            component={component}
            onAdd={(type) => {
              onAddComponent(type);
              onClose();
            }}
          />
        ))}
      </div>
      <div className={styles.paletteModalFooter}>
        <Button onClick={onClose}>Close</Button>
      </div>
    </Modal>
  );
}

export default ComponentPalette;
